'use client';

import {QueryClient} from '@components/@query';
import {useQuestionsStore} from '@components/LiveEvent/@hooks';
import {MutationKey} from '@config/mutationKeys';
import {QueryKey} from '@config/queryKeys';
import {
    IQuestion,
    IQuestionsResponse,
    QuestionsSortByEnum,
} from '@services/live-event/live-event.model';
import LiveEventService from '@services/live-event/live-event.service';
import {useMutation} from '@tanstack/react-query';
import {updateInfiniteQueryData} from '@utils/updateInfiniteQueryData';
import {useCurrentLiveEventId} from './useCurrentLiveEventId';

export const useQuestionUpvoteToggleMutation = (
    qId: IQuestion['id'],
    hasUpVoted: boolean,
) => {
    const eventId = useCurrentLiveEventId();
    const sortByFilter = useQuestionsStore(s => s.sortByFilter);

    const mutation = useMutation({
        mutationKey: [MutationKey.LIVE_EVENT_QUESTION_UPVOTE_TOGGLE(qId)],
        mutationFn: async () => {
            if (hasUpVoted) {
                await LiveEventService.removeUpvoteQuestion(qId);
            } else {
                await LiveEventService.upvoteQuestion(qId);
            }
        },
        onMutate: async () => {
            await QueryClient.cancelQueries({
                queryKey: QueryKey.LIVE_EVENT_QUESTIONS(eventId!, sortByFilter),
            });

            updateInfiniteQueryData(
                QueryKey.LIVE_EVENT_QUESTIONS(eventId!, sortByFilter),
                (data: IQuestionsResponse) => data.webinar_questions,
                questionDetail => questionDetail.id === qId,
                prevItem =>
                    prevItem && {
                        ...prevItem,
                        user_upvoted: !prevItem.user_upvoted,
                        upvote_count: hasUpVoted
                            ? --prevItem.upvote_count
                            : ++prevItem.upvote_count,
                    },
            );
        },
        onSettled: () => {
            Object.values(QuestionsSortByEnum).forEach(sortOrder => {
                QueryClient.invalidateQueries({
                    queryKey: QueryKey.LIVE_EVENT_QUESTIONS(
                        eventId!,
                        sortOrder,
                    ),
                });
            });
        },
        onError: () => {
            updateInfiniteQueryData(
                QueryKey.LIVE_EVENT_QUESTIONS(eventId!, sortByFilter),
                (data: IQuestionsResponse) => data.webinar_questions,
                questionDetail => questionDetail.id === qId,
                prevItem =>
                    prevItem && {
                        ...prevItem,
                    },
            );
        },
    });

    return {mutation};
};
